/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useMemo } from "react";
import useHomepageVersion from "./useHomepageVersion";
import { DEFAULT_HOMEPAGE_VERSION_ID, homepageVersions } from "./versions";

function shouldShowVersionSwitcher() {
  if (typeof window === "undefined") {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  return params.get("homeSwitcher") === "1";
}

export default function HomepageRouter() {
  const { clearVersion, setVersion, source, versionId } = useHomepageVersion({
    defaultVersionId: DEFAULT_HOMEPAGE_VERSION_ID,
    versions: homepageVersions,
  });

  const selectedVersion = homepageVersions[versionId] || homepageVersions[DEFAULT_HOMEPAGE_VERSION_ID];
  const VersionComponent = selectedVersion.component;
  const showSwitcher = useMemo(shouldShowVersionSwitcher, []);

  return (
    <>
      <VersionComponent content={selectedVersion.content} versionMeta={selectedVersion} />
      {showSwitcher && (
        <HomepageVersionSwitcher
          activeVersionId={selectedVersion.id}
          clearVersion={clearVersion}
          source={source}
          versions={homepageVersions}
          onSelectVersion={setVersion}
        />
      )}
    </>
  );
}

function HomepageVersionSwitcher({ activeVersionId, clearVersion, onSelectVersion, source, versions }) {
  function selectVersion(nextVersionId) {
    const didSelect = onSelectVersion(nextVersionId);
    if (!didSelect || typeof window === "undefined") {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set("home", nextVersionId);
    window.history.replaceState(null, "", url.toString());
  }

  function resetVersion() {
    clearVersion();

    if (typeof window === "undefined") {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("home");
    url.searchParams.delete("homepage");
    url.searchParams.delete("homepageVersion");
    window.history.replaceState(null, "", url.toString());
  }

  return (
    <div className="fixed bottom-4 right-4 z-[9999] max-w-[calc(100vw-2rem)] rounded-[8px] border border-white/15 bg-[#05070D]/95 p-3 text-xs text-slate-200 shadow-2xl shadow-black/40 backdrop-blur">
      <div className="mb-2 flex items-center justify-between gap-4">
        <span className="font-semibold text-white">Homepage version</span>
        <span className="rounded-[8px] border border-cyan-300/20 bg-cyan-300/10 px-2 py-1 text-[11px] uppercase text-cyan-100">
          {source}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {Object.values(versions).map((version) => {
          const isActive = version.id === activeVersionId;
          return (
            <button
              key={version.id}
              type="button"
              onClick={() => selectVersion(version.id)}
              className={`rounded-[8px] border px-3 py-2 text-left transition ${
                isActive
                  ? "border-[#00D5FF] bg-[#00D5FF]/15 text-white"
                  : "border-white/10 bg-white/[0.04] text-slate-300 hover:border-white/25 hover:text-white"
              }`}
              title={version.summary}
            >
              <span className="block font-semibold">{version.label}</span>
              <span className="mt-0.5 block text-[11px] uppercase text-slate-500">{version.status}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={resetVersion}
          className="rounded-[8px] border border-white/10 bg-white/[0.04] px-3 py-2 font-semibold text-slate-300 transition hover:border-white/25 hover:text-white"
        >
          Clear
        </button>
      </div>
    </div>
  );
}
